import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageSquare, Save, Plus, Trash2, RefreshCw, Edit2, X, Eye, Users } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { formatLocalDateTime } from "@/utils/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PopupsNotices = () => {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showReadHistory, setShowReadHistory] = useState(false);
  const [selectedPopupHistory, setSelectedPopupHistory] = useState(null);
  const [readHistory, setReadHistory] = useState({ total_reads: 0, read_logs: [] });
  const [formData, setFormData] = useState({
    key: "",
    title: "",
    message: "",
    type: "info",
    display_location: [],
    valid_from: "",
    valid_until: "",
    active: true
  });

  const displayLocationOptions = [
    { value: "dashboard", label: "Dashboard" },
    { value: "flights", label: "Flights Page" },
    { value: "reports", label: "Reports Page" },
    { value: "agencies", label: "Agencies Page" },
    { value: "services", label: "Services Page" },
    { value: "invoices", label: "Invoices Page" },
    { value: "planning", label: "Planning Page" },
    { value: "statistics", label: "Statistics Page" },
    { value: "login", label: "Login Page" },
    { value: "all", label: "All Pages (Global)" }
  ];

  useEffect(() => {
    fetchPopups();
  }, []);

  const fetchPopups = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/popups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPopups(response.data);
    } catch (error) {
      toast.error("Failed to fetch popups");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (editingId) {
        await axios.put(`${API}/popups/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Popup updated successfully");
      } else {
        await axios.post(`${API}/popups`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Popup created successfully");
      }
      fetchPopups();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save popup");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (popup) => {
    setFormData({
      key: popup.key,
      title: popup.title,
      message: popup.message,
      type: popup.type || "info",
      display_location: popup.display_location || [],
      valid_from: popup.valid_from || "",
      valid_until: popup.valid_until || "",
      active: popup.active !== false
    });
    setEditingId(popup.id);
    setShowAddForm(true);
  };

  const handleViewReadHistory = async (popup) => {
    setSelectedPopupHistory(popup);
    setShowReadHistory(true);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/popups/${popup.id}/read-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReadHistory(response.data);
    } catch (error) {
      toast.error("Failed to fetch read history");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this popup?")) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/popups/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Popup deleted successfully");
      fetchPopups();
    } catch (error) {
      toast.error("Failed to delete popup");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      key: "",
      title: "",
      message: "",
      type: "info",
      display_location: [],
      valid_from: "",
      valid_until: "",
      active: true
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "success": return "#10B981";
      case "warning": return "#F59E0B";
      case "error": return "#EF4444";
      default: return "#3B82F6";
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: '#FFFFFF' }}>
            <MessageSquare className="h-8 w-8" style={{ color: '#FFD700' }} />
            Popups and Notices
          </h1>
          <p className="mt-1" style={{ color: '#B0B0B0' }}>
            Manage all system popups, notices, and warning messages
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={fetchPopups}
            disabled={loading}
            style={{ background: '#3B82F6', color: '#FFFFFF' }}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{ background: '#FFD700', color: '#0A1128' }}
          >
            {showAddForm ? <X className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
            {showAddForm ? 'Cancel' : 'Add Popup'}
          </Button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card style={{ background: 'rgba(10, 17, 40, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }} className="mb-6">
          <CardHeader>
            <CardTitle style={{ color: '#FFD700' }}>
              {editingId ? 'Edit Popup' : 'Add New Popup'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="key" style={{ color: '#E8E8E8' }}>Unique Key *</Label>
                  <Input
                    id="key"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                    placeholder="e.g., flight_approval_pending"
                    required
                    disabled={editingId !== null}
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                  />
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                    Unique identifier for this popup (cannot be changed after creation)
                  </p>
                </div>
                <div>
                  <Label htmlFor="type" style={{ color: '#E8E8E8' }}>Type *</Label>
                  <select
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    required
                    className="w-full p-2 rounded"
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8', border: '1px solid #4A5A8A' }}
                  >
                    <option value="info">Info</option>
                    <option value="success">Success</option>
                    <option value="warning">Warning</option>
                    <option value="error">Error</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="title" style={{ color: '#E8E8E8' }}>Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Popup title"
                  required
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                />
              </div>

              <div>
                <Label htmlFor="message" style={{ color: '#E8E8E8' }}>Message *</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Popup message content"
                  required
                  rows={4}
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                />
              </div>

              <div>
                <Label htmlFor="display_location" style={{ color: '#E8E8E8' }}>Display Location *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 p-3 rounded" style={{ background: '#0A1128', border: '1px solid #4A5A8A' }}>
                  {displayLocationOptions.map((option) => (
                    <div key={option.value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`location_${option.value}`}
                        checked={formData.display_location.includes(option.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ 
                              ...formData, 
                              display_location: [...formData.display_location, option.value] 
                            });
                          } else {
                            setFormData({ 
                              ...formData, 
                              display_location: formData.display_location.filter(loc => loc !== option.value) 
                            });
                          }
                        }}
                        className="h-4 w-4"
                        style={{ accentColor: '#FFD700' }}
                      />
                      <Label htmlFor={`location_${option.value}`} className="font-normal cursor-pointer text-sm" style={{ color: '#E8E8E8' }}>
                        {option.label}
                      </Label>
                    </div>
                  ))}
                </div>
                <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                  Select one or more locations where this popup should appear
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="valid_from" style={{ color: '#E8E8E8' }}>Valid From (Optional)</Label>
                  <Input
                    id="valid_from"
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                  />
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                    Start showing popup from this date
                  </p>
                </div>
                <div>
                  <Label htmlFor="valid_until" style={{ color: '#E8E8E8' }}>Valid Until (Optional)</Label>
                  <Input
                    id="valid_until"
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                  />
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                    Stop showing popup after this date
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="h-4 w-4"
                  style={{ accentColor: '#FFD700' }}
                />
                <Label htmlFor="active" className="font-normal cursor-pointer" style={{ color: '#E8E8E8' }}>
                  Active (show this popup to users)
                </Label>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={loading} style={{ background: '#FFD700', color: '#0A1128' }}>
                  <Save className="mr-2 h-4 w-4" />
                  {editingId ? 'Update' : 'Create'} Popup
                </Button>
                <Button type="button" onClick={resetForm} variant="outline" style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Popups List */}
      <div className="space-y-4">
        {loading && !popups.length ? (
          <Card style={{ background: 'rgba(10, 17, 40, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
            <CardContent className="p-6 text-center" style={{ color: '#B0B0B0' }}>
              Loading popups...
            </CardContent>
          </Card>
        ) : popups.length === 0 ? (
          <Card style={{ background: 'rgba(10, 17, 40, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
            <CardContent className="p-6 text-center" style={{ color: '#B0B0B0' }}>
              No popups found. Click "Add Popup" to create one.
            </CardContent>
          </Card>
        ) : (
          popups.map((popup) => (
            <Card key={popup.id} style={{ background: 'rgba(10, 17, 40, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div 
                        className="px-3 py-1 rounded text-xs font-semibold"
                        style={{ 
                          background: `${getTypeColor(popup.type)}20`,
                          color: getTypeColor(popup.type),
                          border: `1px solid ${getTypeColor(popup.type)}`
                        }}
                      >
                        {popup.type?.toUpperCase()}
                      </div>
                      <span className="text-xs font-mono" style={{ color: '#B0B0B0' }}>
                        {popup.key}
                      </span>
                      {!popup.active && (
                        <span className="px-2 py-1 rounded text-xs" style={{ background: '#EF444420', color: '#EF4444', border: '1px solid #EF4444' }}>
                          INACTIVE
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold mb-1" style={{ color: '#FFFFFF' }}>
                      {popup.title}
                    </h3>
                    <p className="text-sm whitespace-pre-wrap mb-2" style={{ color: '#B0B0B0' }}>
                      {popup.message}
                    </p>
                    {popup.display_location && popup.display_location.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs" style={{ color: '#B0B0B0' }}>Display on:</span>
                        {popup.display_location.map((loc) => {
                          const locationLabel = displayLocationOptions.find(opt => opt.value === loc)?.label || loc;
                          return (
                            <span 
                              key={loc}
                              className="px-2 py-1 rounded text-xs"
                              style={{ background: '#3B82F620', color: '#3B82F6', border: '1px solid #3B82F6' }}
                            >
                              {locationLabel}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {(popup.valid_from || popup.valid_until) && (
                      <div className="mt-2 text-xs" style={{ color: '#B0B0B0' }}>
                        Valid: {popup.valid_from || 'Always'} → {popup.valid_until || 'Forever'}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      onClick={() => handleViewReadHistory(popup)}
                      variant="outline"
                      size="sm"
                      style={{ borderColor: '#4A5A8A', color: '#10B981' }}
                      title="View read history"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handleEdit(popup)}
                      variant="outline"
                      size="sm"
                      style={{ borderColor: '#4A5A8A', color: '#3B82F6' }}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(popup.id)}
                      variant="outline"
                      size="sm"
                      style={{ borderColor: '#4A5A8A', color: '#EF4444' }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Read History Dialog */}
      <Dialog open={showReadHistory} onOpenChange={setShowReadHistory}>
        <DialogContent style={{ background: '#0A1128', borderColor: '#4A5A8A', maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700' }}>
              <Users className="inline mr-2 h-5 w-5" />
              Read History: {selectedPopupHistory?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="mb-4 p-3 rounded" style={{ background: '#1E2A4A', border: '1px solid #4A5A8A' }}>
              <p className="text-sm" style={{ color: '#E8E8E8' }}>
                <strong>Total Reads:</strong> {readHistory.total_reads}
              </p>
            </div>
            {readHistory.read_logs && readHistory.read_logs.length > 0 ? (
              <div className="space-y-2">
                {readHistory.read_logs.map((log, index) => (
                  <div 
                    key={log.id || index}
                    className="p-3 rounded flex justify-between items-center"
                    style={{ background: '#1E2A4A', border: '1px solid #4A5A8A' }}
                  >
                    <div>
                      <p className="font-semibold" style={{ color: '#FFFFFF' }}>{log.user_name}</p>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>{log.user_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs px-2 py-1 rounded inline-block" style={{ background: '#3B82F620', color: '#3B82F6', border: '1px solid #3B82F6' }}>
                        {log.user_role}
                      </p>
                      <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                        {formatLocalDateTime(log.read_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-8" style={{ color: '#B0B0B0' }}>
                No one has read this popup yet
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PopupsNotices;
